import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import { useAuth } from 'contexts/auth-context';
import { roleToNumber } from 'src/app/auth/roles.client';
import { InternalRole } from '../../auth/endpoints/MeEndpoint.types';
import { Table } from 'components/table/Table';
import { APIOrder } from 'src/api/v1/order.types';
import { APISupplier } from 'src/api/v1/supplier.types';
import { formatNumber } from '@utils/number';
import { Pagination } from 'components/pagination/Pagination';

import { useDashboardContext } from '../context';
import { APICustomer } from 'src/api/v1/customer.types';
import { Button } from 'components/button/Button';
import { DownloadSimpleIcon } from '@phosphor-icons/react';
import { displayError } from 'src/app/toast';
import { SimpleSelect, ISimpleSelectItem } from 'components/select/SimpleSelect';
import { ChartWithTable, ChartWithTableEntry } from '../../../components/graphs/chart-with-table';
import { TruckReportStatsResponse } from '../endpoints/TruckReportStatsEndpoint.types';
import { DelayedSpinner } from 'components/Spinner';
import { StatCard } from 'components/stat-card/StatCard';

const BUSINESS_UNIT_PARAM: Record<string, string> = {
  Benelux: '0',
  France: '1',
  Germany: '2',
  Forwarding: '3',
};

export function ReportStatsV2() {
  const { me } = useAuth();
  const [selectedSupplier, setSelectedSupplier] = useState<ISimpleSelectItem | null>(null);
  const [selectedTruck, setSelectedTruck] = useState<ISimpleSelectItem | null>(null);
  const [orders, setOrders] = useState<APIOrder[]>([]);
  const [suppliers, setSuppliers] = useState<APISupplier[]>([]);
  const [customers, setCustomers] = useState<APICustomer[]>([]);
  const [stats, setStats] = useState<TruckReportStatsResponse | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  const { timeFrame, businessUnit } = useDashboardContext();

  const pageSize = 20;
  const [page, setPage] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Permissions — checked after all hooks
  const meRoleNumber = roleToNumber(me.role);
  const adminRoleNumber = roleToNumber(InternalRole.InternalAdmin);
  const isAdmin = meRoleNumber >= adminRoleNumber;

  // Fetch suppliers for dropdown
  useEffect(() => {
    const getSuppliers = async () => {
      const response = await fetch(`/api/v1/legacy/suppliers?regularCarrier=true`);
      if (!response.ok) {
        displayError('Er is iets misgegaan bij het ophalen van de vervoerders.');
        return;
      }
      const data = await response.json();
      setSuppliers(data.map((x: any) => new APISupplier(x)));
    };
    getSuppliers();
  }, []);

  // Fetch aggregated stats
  useEffect(() => {
    if (!timeFrame?.startDate || !timeFrame?.endDate) return;

    const fetchStats = async () => {
      setIsLoadingStats(true);
      const params: string[][] = [
        ['from', timeFrame.startDate],
        ['to', timeFrame.endDate],
      ];
      if (selectedSupplier) {
        params.push(['supplierId', selectedSupplier.key]);
      }
      if (selectedTruck) {
        params.push(['truckLicensePlate', selectedTruck.key]);
      }
      if (businessUnit) {
        params.push(['businessUnit', businessUnit]);
      }

      const response = await fetch(`/api/v1/stats/truck-report?${new URLSearchParams(params).toString()}`);
      if (response.ok) {
        setStats(await response.json());
      } else {
        displayError('Er is iets misgegaan bij het ophalen van de statistieken.');
      }
      setIsLoadingStats(false);
    };

    fetchStats();
  }, [timeFrame, businessUnit, selectedSupplier, selectedTruck]);

  // Supplier dropdown items — only fixed carriers (vaste vervoerders)
  const supplierItems: ISimpleSelectItem[] = useMemo(
    () =>
      suppliers
        .filter((s) => s.regularCarrier)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((s) => ({ key: String(s.id), name: s.name })),
    [suppliers],
  );

  // Truck dropdown items (filtered by selected supplier)
  const truckItems: ISimpleSelectItem[] = useMemo(() => {
    if (!selectedSupplier) return [];
    const supplier = suppliers.find((s) => String(s.id) === selectedSupplier.key);
    if (!supplier?.trucks) return [];
    return supplier.trucks.filter((t) => t.isActive).map((t) => ({ key: t.licensePlate, name: t.licensePlate }));
  }, [selectedSupplier, suppliers]);

  // Build order query params
  const buildOrderParams = useCallback(
    (extra: string[][] = []): string[][] => {
      const params: string[][] = [
        ['status', 'executed'],
        ['regular_carrier', 'true'],
        ['from', timeFrame?.startDate || ''],
        ['to', timeFrame?.endDate || ''],
        ...extra,
      ];
      if (businessUnit && BUSINESS_UNIT_PARAM[businessUnit]) {
        params.push(['businessUnit', BUSINESS_UNIT_PARAM[businessUnit]]);
      }
      if (selectedSupplier) {
        params.push(['supplier', selectedSupplier.key]);
      }
      if (selectedTruck) {
        params.push(['truck_licenseplate', selectedTruck.key]);
      }
      return params;
    },
    [timeFrame, businessUnit, selectedSupplier, selectedTruck],
  );

  // Fetch orders (debounced)
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const fetchOrders = async () => {
        setIsFetching(true);

        const params = buildOrderParams([
          ['limit', pageSize.toString()],
          ['offset', String(page * pageSize)],
        ]);
        const nextParams = buildOrderParams([
          ['limit', pageSize.toString()],
          ['offset', String((page + 1) * pageSize)],
        ]);

        try {
          const [response, nextResponse] = await Promise.all([
            fetch(`/api/v1/legacy/orders?${new URLSearchParams(params).toString()}`),
            fetch(`/api/v1/legacy/orders?${new URLSearchParams(nextParams).toString()}`),
          ]);

          if (!response.ok) {
            displayError('Failed to fetch orders', 'Er is iets misgegaan bij het ophalen van de orders.');
            return;
          }

          const data = await response.json();
          const fetchedOrders = data.map((x: any) => new APIOrder(x));
          setOrders(fetchedOrders);

          if (nextResponse.ok) {
            const nextData = await nextResponse.json();
            setHasNext(nextData.length > 0);
          }

          // Fetch unique customers
          const customerIds = [
            ...new Set(fetchedOrders.map((o: APIOrder) => o.customerId).filter(Boolean)),
          ] as string[];
          const customerResponses = await Promise.all(
            customerIds.map((id) => fetch(`/api/v1/legacy/customers/${id}`).then((r) => (r.ok ? r.json() : null))),
          );
          setCustomers(customerResponses.filter(Boolean).map((c: any) => new APICustomer(c)));
        } finally {
          setIsFetching(false);
        }
      };

      fetchOrders();
    }, 250);

    return () => clearTimeout(debounceRef.current);
  }, [page, buildOrderParams]);

  // Reset truck when supplier changes
  useEffect(() => {
    setSelectedTruck(null);
  }, [selectedSupplier]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [selectedSupplier, selectedTruck]);

  // Chart data — single memo instead of 5 identical ones
  const chartData = useMemo(() => {
    if (!stats) return null;

    const mapSuppliers = (
      valueKey: 'totalRevenue' | 'totalMargin' | 'marginPercentage' | 'totalKilometers' | 'orderCount',
    ): ChartWithTableEntry[] =>
      stats.suppliers.map((s) => ({
        id: s.supplierId,
        name: s.supplierName,
        value: s[valueKey],
        link: `/internal/suppliers/${s.supplierId}/general`,
      }));

    return {
      revenue: mapSuppliers('totalRevenue'),
      margin: mapSuppliers('totalMargin'),
      marginPercentage: mapSuppliers('marginPercentage'),
      kilometers: mapSuppliers('totalKilometers'),
      orders: mapSuppliers('orderCount'),
    };
  }, [stats]);

  // O(1) lookups for dataMapper
  const supplierMap = useMemo(() => new Map(suppliers.map((s) => [s.id, s])), [suppliers]);
  const customerMap = useMemo(() => new Map(customers.map((c) => [c.id, c])), [customers]);

  const dataMapper = (order: APIOrder) => {
    const firstLoadStop = order.lines.flatMap((line) => line.stops).find((stop) => stop.type === 'Load');
    const firstUnloadStop = order.lines.flatMap((line) => line.stops).find((stop) => stop.type === 'Unload');

    const profitPercentage =
      order.totalSales && order.totalPurchases
        ? ((order.totalSales - order.totalPurchases) / order.totalSales) * 100
        : 0;

    return [
      <Link to={`/internal/orders/${order.id}/general`}>{order.orderNumber || '-'}</Link>,
      <Link to={`/internal/suppliers/${order.supplierId}/general`}>
        {supplierMap.get(order.supplierId as number)?.name || '-'}
      </Link>,
      order.truck ? (
        <Link to={`/internal/suppliers/${order.supplierId}/trucks/${order.truck.id}`}>
          {order.truck.licensePlate || '-'}
        </Link>
      ) : (
        '-'
      ),
      <span>{formatNumber(((order.totalDrivenMeters ?? order.totalFullMeters) || 0) / 1000, 2, '.', ',')} km</span>,
      <span>{order.totalPurchases ? `€ ${formatNumber(order.totalPurchases / 10_000, 2, ',', '.')}` : '-'}</span>,
      <span>{order.totalSales ? `€ ${formatNumber(order.totalSales / 10_000, 2, ',', '.')}` : '-'}</span>,
      <span className={profitPercentage < 0 ? 'text-negative-text' : ''}>
        {profitPercentage ? `${formatNumber(profitPercentage, 1, ',', '.')}%` : '-'}
      </span>,
      <Link to={`/internal/customers/${order.customerId}/general`}>
        {customerMap.get(order.customerId as number)?.name || '-'}
      </Link>,
      <span>{firstLoadStop?.location.name}</span>,
      <span>{firstLoadStop?.location.country}</span>,
      <span>{firstLoadStop?.location.postalCode}</span>,
      <span>{firstLoadStop ? new Date(firstLoadStop.date).toLocaleDateString() : '-'}</span>,
      <span>{firstUnloadStop?.location.name}</span>,
      <span>{firstUnloadStop?.location.country}</span>,
      <span>{firstUnloadStop?.location.postalCode}</span>,
      <span>{firstUnloadStop ? new Date(firstUnloadStop.date).toLocaleDateString() : '-'}</span>,
    ];
  };

  // Permission gate — after all hooks
  if (!isAdmin) {
    return <Navigate to="/internal/stats/sales" replace />;
  }

  const downloadReport = async () => {
    try {
      const params = buildOrderParams([['stats', 'true']]);
      const res = await fetch(`/api/v1/legacy/orders?${new URLSearchParams(params).toString()}`, {
        headers: { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      });
      if (!res.ok) {
        displayError(
          'Failed to download truck report',
          'Er is een fout opgetreden bij het downloaden van het truckrapport',
        );
        return;
      }
      const blob = await res.blob();
      // eslint-disable-next-line no-restricted-globals
      const url = window.URL.createObjectURL(blob);
      // eslint-disable-next-line no-restricted-globals
      const a = document.createElement('a');
      a.href = url;
      a.download = `truck-report-internal-${timeFrame?.startDate}-${timeFrame?.endDate}.xlsx`;
      // eslint-disable-next-line no-restricted-globals
      document.body.appendChild(a);
      a.click();
      a.remove();
      // eslint-disable-next-line no-restricted-globals
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // @ts-ignore
      displayError(error, `Er is een fout opgetreden bij het aanmaken van het truckrapport: ${error}`);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="heading-two">Intern truckrapport</h2>
        <Button iconLeft={<DownloadSimpleIcon className="button-icon" />} onTrigger={downloadReport}>
          Intern truckrapport exporteren
        </Button>
      </div>

      {/* Summary Dashboard Cards */}
      {isLoadingStats && <DelayedSpinner size={6} className="flex justify-center py-4" />}

      {stats && (
        <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
          <StatCard label="Totaal Orders" value={String(stats.summary.totalOrders)} />
          <StatCard
            label="Totaal Kilometers"
            value={formatNumber(stats.summary.totalKilometers, 0, '.', ',')}
            unit="km"
          />
          <StatCard
            label="Totaal Omzet"
            value={`€ ${formatNumber(stats.summary.totalRevenue, 2, ',', '.')}`}
            subValue={`Inkoop: € ${formatNumber(stats.summary.totalPurchase, 2, ',', '.')}`}
          />
          <StatCard
            label="Bruto Marge"
            value={`€ ${formatNumber(stats.summary.totalMargin, 2, ',', '.')}`}
            subValue={`${formatNumber(stats.summary.marginPercentage, 1, ',', '.')}%`}
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full max-w-64">
          <div className="label-text mb-1.5">Vervoerder</div>
          <SimpleSelect
            items={supplierItems}
            selectedItem={selectedSupplier}
            onSelect={setSelectedSupplier}
            isOptional={true}
          />
        </div>

        <div className="w-full max-w-48">
          <div className="label-text mb-1.5">Nummerplaat</div>
          <SimpleSelect
            items={truckItems}
            selectedItem={selectedTruck}
            onSelect={setSelectedTruck}
            isOptional={true}
            isDisabled={!selectedSupplier}
          />
        </div>
      </div>

      {/* Charts - Carrier Comparison */}
      {chartData && stats && stats.suppliers.length > 1 && (
        <div className="flex flex-col gap-4">
          <h3 className="heading-three">Vergelijking onderaannemers</h3>
          <div className="grid gap-4 2xl:grid-cols-2 2xl:gap-6">
            <div>
              <ChartWithTable
                title="Omzet per vervoerder"
                nameTitle="Vervoerder"
                data={chartData.revenue}
                dataType="currency"
                showPercentage={true}
              />
            </div>

            <div>
              <ChartWithTable
                title="Marge per vervoerder"
                nameTitle="Vervoerder"
                data={chartData.margin}
                dataType="currency"
                showPercentage={true}
              />
            </div>

            <div>
              <ChartWithTable
                title="Marge % per vervoerder"
                nameTitle="Vervoerder"
                data={chartData.marginPercentage}
                dataType="percentage"
                showPercentage={true}
                totalAverage={stats.summary.marginPercentage}
              />
            </div>

            <div>
              <ChartWithTable
                title="Kilometers per vervoerder"
                nameTitle="Vervoerder"
                data={chartData.kilometers}
                dataType="value"
                showPercentage={true}
                precision={0}
              />
            </div>

            <div>
              <ChartWithTable
                title="Aantal orders per vervoerder"
                nameTitle="Vervoerder"
                data={chartData.orders}
                dataType="value"
                showPercentage={true}
                precision={0}
              />
            </div>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <h3 className="heading-four">Orders</h3>

      <Table
        headers={[
          { id: 'reference', name: 'Order Nr', width: '9em' },
          { id: 'supplierName', name: 'Vervoerder' },
          { id: 'truckName', name: 'Nummerplaat' },
          { id: 'fullKms', name: 'Kilometers' },
          { id: 'buyPrice', name: 'Aankooptotaal', width: '7em' },
          { id: 'salePrice', name: 'Facturatietotaal', width: '7em' },
          { id: 'grossMargin', name: 'Brutomarge' },
          { id: 'clientName', name: 'Klant' },
          { id: 'loadingLocation', name: 'Laadplaats' },
          { id: 'loadingCountry', name: 'Land laadplaats' },
          { id: 'loadingPostalCode', name: 'Postcode laadplaats' },
          { id: 'loadingDate', name: 'Datum laden' },
          { id: 'unloadingLocation', name: 'Bestemming' },
          { id: 'unloadingCountry', name: 'Land losplaats' },
          { id: 'unloadingPostalCode', name: 'Postcode losplaats' },
          { id: 'unloadingDate', name: 'Datum lossen' },
        ]}
        data={orders}
        mapData={dataMapper}
        idKey="id"
        isMultiSelect={false}
      />

      {orders.length === 0 && <div className="text-sm font-medium text-fg">Geen orders gevonden</div>}

      <div className="flex justify-end">
        <Pagination
          hasNext={hasNext}
          hasPrevious={page > 0}
          isFetching={isFetching}
          next={() => setPage(page + 1)}
          previous={() => setPage(page - 1)}
        />
      </div>
    </div>
  );
}
